/**********************************************************************************************************
 *   BASE IMPORTS
 **********************************************************************************************************/
import { useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'
import styled from 'styled-components'

/**********************************************************************************************************
 *   SHARED IMPORTS
 **********************************************************************************************************/
import { Anchor, Card } from '@nexigendigital/nxui'
import { serviceAPI, useCancelChangePlanRequestMutation } from 'api/service'
import { ChangePlanStatus } from 'models/enums'
import { parseSQL } from 'helpers/utils'

import BillingCycleLightbox from 'containers/services/lightbox/billingCycleLightbox'
import RenewLightbox from 'containers/services/lightbox/renewLightbox'
import ChangeServicePlanLightbox from 'containers/services/lightbox/changeServicePlanLightbox'

/**********************************************************************************************************
 *   INTERFACE
 **********************************************************************************************************/
interface Lightbox {
    status: boolean,
    template: 'none' | 'renew' | 'billing-cycle' | 'change-plan'
}

/**********************************************************************************************************
 *   COMPONENT START
 **********************************************************************************************************/
export default function Information() {
    const location = useLocation()
    const id = +location.pathname?.split('/')[2]
    const {
        isLoading,
        isError,
        data = { name: '', next_due_date: '', billing_cycle: { name: '' }, product: { name: '' }, status: '' }
    } = serviceAPI.endpoints.service.useQueryState(id)
    const { data: changePlanStatusData } = serviceAPI.endpoints.changePlanStatus.useQueryState(id)
    const [cancelChangePlanRequest] = useCancelChangePlanRequestMutation()
    const [lightbox, setLightbox] = useState<Lightbox>({ status: false, template: 'none' })
    const columns = useMemo(() => ([
        {
            Header: 'Information',
            accessor: 'information'
        },
        {
            Header: 'name',
            accessor: 'name'
        },
        {
            Header: '',
            accessor: 'action',
            align: 'right'
        }
    ]), [])
    const service = useMemo(() => {
        if (isLoading || isError) {
            return []
        }

        const changePlanAction = changePlanStatusData?.status === ChangePlanStatus.Pending
            ? (
                <Anchor color={'primary'} onClick={() => cancelChangePlanRequest(id)}>
                    Cancel
                </Anchor>
            )
            : (
                <Anchor color={'primary'} onClick={() => setLightbox({ status: true, template: 'change-plan' })}>
                    Change
                </Anchor>
            )

        return [
            {
                information: 'Hosting Plan',
                name: data.product.name,
                action: changePlanAction
            },
            {
                information: 'Primary Domain Name',
                name: data?.name
            },
            {
                information: 'Next Due Date',
                name: data?.next_due_date ? parseSQL(data.next_due_date).setLocale('en-AU').toLocaleString() : null,
                action: (
                    <Anchor
                        color={'primary'}
                        onClick={() => setLightbox({ status: true, template: 'renew' })}
                    >
                        Renew
                    </Anchor>
                )
            },
            {
                information: 'Billing Cycle',
                name: data?.billing_cycle?.name,
                action: <Anchor color={'primary'} onClick={() => setLightbox({
                    status: true,
                    template: 'billing-cycle'
                })}>Change</Anchor>
            }
        ]
    }, [data?.name, data?.product?.name, data?.billing_cycle?.name, isLoading, isError, changePlanStatusData?.status])

    return (
        <>
            <InformationWrapper
                table={{
                    conditions: {
                        loading: isLoading && 'Loading information...',
                        sort: false
                    },
                    render: {
                        columns: () => columns,
                        data: () => service
                    }
                }}
            />
            <BillingCycleLightbox
                id={id}
                status={lightbox.status && lightbox.template === 'billing-cycle'}
                close={() => setLightbox({ status: false, template: 'none' })}
            />
            <RenewLightbox
                id={id}
                status={lightbox.status && lightbox.template === 'renew'}
                close={() => setLightbox({ status: false, template: 'none' })}
            />
            <ChangeServicePlanLightbox
                id={id}
                status={lightbox.status && lightbox.template === 'change-plan'}
                close={() => setLightbox({ status: false, template: 'change-plan' })}
            />
        </>
    )
}

/**********************************************************************************************************
 *   CUSTOM STYLING
 **********************************************************************************************************/
const InformationWrapper = styled(Card)`
    width: 100%;
`
